import { useState, useEffect } from 'react'
import {
  Modal,
  Form,
  Select,
  DatePicker,
  InputNumber,
  Input,
  message,
  Space,
  Avatar,
} from 'antd'
import { UserOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

interface Doctor {
  id: string
  userId: string
  name: string
  specialty: string | null
}

interface Patient {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  phone: string | null
  image: string | null
  name: string
}

interface ScheduleConsultationModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  patient?: Patient | null  // If provided, skip patient search
  patientId?: string        // If provided without patient, fetch patient info
}

export function ScheduleConsultationModal({
  open,
  onClose,
  onSuccess,
  patient: initialPatient,
  patientId,
}: ScheduleConsultationModalProps) {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [doctors, setDoctors] = useState<Doctor[]>([])
  const [doctorsLoading, setDoctorsLoading] = useState(false)
  const [patients, setPatients] = useState<Patient[]>([])
  const [patientsLoading, setPatientsLoading] = useState(false)
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null)

  // If patient is provided, use it; otherwise allow patient search
  const hasPreselectedPatient = !!initialPatient || !!patientId

  // Fetch doctors when modal opens
  useEffect(() => {
    if (open) {
      fetchDoctors()
      form.resetFields()

      // Set default scheduled time to next hour
      const nextHour = dayjs().add(1, 'hour').startOf('hour')
      form.setFieldsValue({
        scheduledAt: nextHour,
        duration: 30,
      })

      // If we have a patient, set it
      if (initialPatient) {
        form.setFieldsValue({ patientId: initialPatient.id })
      } else if (patientId) {
        form.setFieldsValue({ patientId })
      }
    }
  }, [open, form, initialPatient, patientId])

  const fetchDoctors = async () => {
    setDoctorsLoading(true)
    try {
      const token = localStorage.getItem('admin_access_token')
      const response = await fetch(`${API_URL}/admin/workflow/doctors`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
      if (response.ok) {
        const data = await response.json()
        setDoctors(data)
      }
    } catch (error) {
      console.error('Failed to fetch doctors:', error)
    } finally {
      setDoctorsLoading(false)
    }
  }

  const searchPatients = async (query: string) => {
    if (!query || query.length < 2) {
      setPatients([])
      return
    }

    setPatientsLoading(true)
    try {
      const token = localStorage.getItem('admin_access_token')
      const response = await fetch(
        `${API_URL}/admin/patients/search?q=${encodeURIComponent(query)}&limit=10`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      )
      if (response.ok) {
        const data = await response.json()
        setPatients(data)
      }
    } catch (error) {
      console.error('Failed to search patients:', error)
    } finally {
      setPatientsLoading(false)
    }
  }

  const handlePatientSearch = (value: string) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout)
    }
    const timeout = setTimeout(() => {
      searchPatients(value)
    }, 300)
    setSearchTimeout(timeout)
  }

  const handleSubmit = async (values: {
    patientId: string
    doctorId: string
    scheduledAt: dayjs.Dayjs
    duration: number
    reason?: string
  }) => {
    setLoading(true)
    try {
      const token = localStorage.getItem('admin_access_token')
      const response = await fetch(`${API_URL}/admin/consultations/standalone`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patientId: values.patientId,
          doctorId: values.doctorId,
          scheduledAt: values.scheduledAt.toISOString(),
          duration: values.duration,
          reason: values.reason,
        }),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.message || 'Neuspešno zakazivanje konsultacije')
      }

      message.success('Video konsultacija uspešno zakazana')
      onSuccess()
      onClose()
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Greška pri zakazivanju')
    } finally {
      setLoading(false)
    }
  }

  const getPatientLabel = () => {
    if (initialPatient) {
      return initialPatient.name
    }
    return null
  }

  return (
    <Modal
      title="Zakaži video konsultaciju"
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Zakaži"
      cancelText="Otkaži"
      confirmLoading={loading}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        {/* Patient selector - only show if no patient preselected */}
        {hasPreselectedPatient ? (
          <Form.Item name="patientId" hidden>
            <Input />
          </Form.Item>
        ) : (
          <Form.Item
            name="patientId"
            label="Pacijent"
            rules={[{ required: true, message: 'Molimo izaberite pacijenta' }]}
          >
            <Select
              showSearch
              placeholder="Pretraži pacijente..."
              filterOption={false}
              onSearch={handlePatientSearch}
              loading={patientsLoading}
              notFoundContent={patientsLoading ? 'Učitavanje...' : 'Nema rezultata'}
              options={patients.map(p => ({
                value: p.id,
                label: (
                  <Space>
                    <Avatar
                      src={p.image}
                      icon={!p.image && <UserOutlined />}
                      size="small"
                    />
                    <span>{p.name}</span>
                    <span style={{ color: '#999' }}>{p.email}</span>
                  </Space>
                ),
              }))}
            />
          </Form.Item>
        )}

        {/* Show patient info if preselected */}
        {hasPreselectedPatient && getPatientLabel() && (
          <Form.Item label="Pacijent">
            <Input value={getPatientLabel() || ''} disabled />
          </Form.Item>
        )}

        <Form.Item
          name="doctorId"
          label="Lekar"
          rules={[{ required: true, message: 'Molimo izaberite lekara' }]}
        >
          <Select
            placeholder="Izaberite lekara..."
            loading={doctorsLoading}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={doctors.map(doctor => ({
              value: doctor.id,
              label: `${doctor.name}${doctor.specialty ? ` (${doctor.specialty})` : ''}`,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="scheduledAt"
          label="Datum i vreme"
          rules={[{ required: true, message: 'Molimo izaberite datum i vreme' }]}
        >
          <DatePicker
            showTime={{ format: 'HH:mm', minuteStep: 15 }}
            format="DD.MM.YYYY HH:mm"
            style={{ width: '100%' }}
            disabledDate={(current) => current && current < dayjs().startOf('day')}
          />
        </Form.Item>

        <Form.Item
          name="duration"
          label="Trajanje (minuta)"
          rules={[{ required: true, message: 'Molimo unesite trajanje' }]}
        >
          <Select
            options={[
              { value: 15, label: '15 minuta' },
              { value: 30, label: '30 minuta' },
              { value: 45, label: '45 minuta' },
              { value: 60, label: '60 minuta' },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="reason"
          label="Razlog konsultacije (opciono)"
        >
          <Input.TextArea
            rows={3}
            placeholder="Npr: Kontrolni pregled, konsultacija o rezultatima..."
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
